import { useState } from 'react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import QRCode from 'qrcode';
import type { FinancialStatementType } from '../types/reports';
import { CurrencyCode } from '../config/currency';
import { useCurrency } from './useCurrency';

interface ExportOptions {
  type: FinancialStatementType;
  format: 'pdf' | 'excel';
  data: any;
  title: string;
  organization: any;
  generatedBy: string;
  isAudited: boolean;
  currency: CurrencyCode;
}

export function useFinancialExport() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { format: formatCurrencyHook } = useCurrency();

  const handleExport = async (options: ExportOptions) => {
    setLoading(true);
    setError(null);

    try {
      if (!validateData(options.data, options.type)) {
        throw new Error('Les données sont invalides ou incomplètes');
      }

      if (!options.organization?.name || !options.organization?.registrationNumber) {
        throw new Error("Les informations de l'entreprise sont incomplètes");
      }

      const formatAmount = (amount: number | undefined) => {
        if (typeof amount !== 'number') return '-';
        return formatCurrencyHook(amount, options.currency);
      };

      if (options.format === 'pdf') {
        await exportToPDF(options, formatAmount);
      } else {
        await exportToExcel(options);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Une erreur est survenue lors de l'export";
      setError(message);
      console.error("Export failed:", message, err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    handleExport
  };
}

function validateData(data: any, type: FinancialStatementType): boolean {
  if (!data) return false;

  switch (type) {
    case 'balance':
      // Supporte le format adapté (fixedAssets) ET le format brut backend (actif/passif)
      return !!(
        (data.fixedAssets?.total && data.currentAssets?.total && data.equity?.total && data.totalAssets) ||
        (data.actif && data.passif)
      );
    case 'income':
      return !!(
        (data.operatingIncome?.total && data.operatingExpenses?.total && data.netResult) ||
        (data.produitsExploitation && data.chargesExploitation)
      );
    case 'cashflow':
      return !!(
        (data.operatingActivities?.total && data.netCashChange !== undefined) ||
        (data.fluxExploitation)
      );
    case 'trial-balance':
      return !!(data.items || data.byClass || Array.isArray(data)) && (!!data.totals || Array.isArray(data));
    default:
      return true;
  }
}

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(err);
    img.src = url;
  });
};

async function exportToPDF(
  options: ExportOptions,
  formatAmount: (amount: number | undefined) => string
): Promise<void> {
  const doc = new jsPDF({ orientation: 'p', unit: 'mm', format: 'a4' });
  doc.setLanguage('fr');

  const styles = {
    fontSize: {
      title: 16,
      subtitle: 11,
      heading: 10,
      normal: 9,
      small: 8,
      tiny: 7,
    },
    colors: {
      primary: '#111827',
      secondary: '#4B5563',
      accent: '#374151',
      tableHeader: '#F3F4F6',
      tableBorder: '#E5E7EB',
      tableRow: '#FFFFFF',
      tableRowAlt: '#F9FAFB',
      highlight: '#374151',
    },
    margins: {
      page: 15,
    },
  };

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = styles.margins.page;
  
  let y = margin;

  // --- HEADER ---
  const maxHeaderHeight = pageHeight / 5;

  // 1. QR Code (right aligned)
  const authString = JSON.stringify({
    org: options.organization.registrationNumber,
    type: options.type,
    date: new Date().toISOString(),
    generator: options.generatedBy,
    hash: Math.random().toString(36).substring(2, 15),
  });
  const encodedAuth = btoa(authString);
  const qrValue = `https://verify.wanzo.cd/auth/${encodedAuth}`;
  
  const qrSize = 22;
  const qrX = pageWidth - margin - qrSize;
  
  try {
    const qrCodeDataUrl = await QRCode.toDataURL(qrValue, {
        errorCorrectionLevel: 'H', type: 'image/png', margin: 1, width: 128
    });
    doc.addImage(qrCodeDataUrl, 'PNG', qrX, y, qrSize, qrSize);
    doc.setFontSize(styles.fontSize.tiny);
    doc.setTextColor(styles.colors.secondary);
    const authText = 'Authentification';
    const authTextWidth = doc.getTextWidth(authText);
    doc.text(authText, qrX + (qrSize - authTextWidth) / 2, y + qrSize + 3);
  } catch (err) {
    console.error("Failed to generate QR code", err);
  }

  // 2. Organization Info & Logo (left aligned)
  let textX = margin;
  let logoHeight = 0;

  if (options.organization.logo) {
    try {
      const logoImg = await loadImage(options.organization.logo);
      const aspectRatio = logoImg.width / logoImg.height;
      logoHeight = 20;
      let logoWidth = logoHeight * aspectRatio;
      if (logoWidth > 40) {
          logoWidth = 40;
          logoHeight = logoWidth / aspectRatio;
      }
      doc.addImage(logoImg, 'PNG', margin, y, logoWidth, logoHeight);
      textX += logoWidth + 5;
    } catch (e) {
      console.error("Could not load organization logo", e);
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(styles.fontSize.heading);
  doc.setTextColor(styles.colors.primary);
  doc.text(options.organization.name.toUpperCase(), textX, y + 5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.small);
  doc.setTextColor(styles.colors.secondary);

  const ids = [
      options.organization.registrationNumber ? `RCCM: ${options.organization.registrationNumber}` : null,
      options.organization.taxId ? `ID. NAT: ${options.organization.taxId}` : null,
  ].filter(Boolean).join(' | ');
  doc.text(ids, textX, y + 10);

  const address = [
      options.organization.address,
      options.organization.phone ? `Tél: ${options.organization.phone}` : null,
  ].filter(Boolean).join(' | ');
  doc.text(address, textX, y + 15);

  y = Math.max(y + logoHeight, y + qrSize) + 8;

  // --- TITLE ---
  doc.setDrawColor(styles.colors.tableBorder);
  doc.setLineWidth(0.2);
  doc.line(margin, y, pageWidth - margin, y);
  y += 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(styles.fontSize.title);
  doc.setTextColor(styles.colors.primary);
  doc.text(options.title, pageWidth / 2, y, { align: 'center' });
  y += 7;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(styles.fontSize.subtitle);
  const exerciceDate = options.data?.exercice || new Date().getFullYear().toString();
  doc.text(`Exercice clos le 31 décembre ${exerciceDate}`, pageWidth / 2, y, { align: 'center' });
  
  const genDate = `Généré le ${new Date().toLocaleDateString('fr-FR')}`;
  doc.setFontSize(styles.fontSize.small);
  doc.text(genDate, pageWidth - margin, y, { align: 'right' });
  y += 10;

  if (y > maxHeaderHeight) {
      console.warn(`Header height (${y}mm) exceeds max height (${maxHeaderHeight}mm).`);
  }

  // --- CONTENT ---
  switch (options.type) {
    case 'balance':
      y = addBalanceSheetContent(doc, options.data, y, styles, formatAmount);
      break;
    case 'income':
      y = addIncomeStatementContent(doc, options.data, y, styles, formatAmount);
      break;
    case 'cashflow':
      y = addCashFlowContent(doc, options.data, y, styles, formatAmount);
      break;
    case 'trial-balance':
      y = addTrialBalanceContent(doc, options.data, y, styles, formatAmount);
      break;
  }

  // --- FOOTER ---
  const footerY = pageHeight - margin + 10;
  doc.setDrawColor(styles.colors.tableBorder);
  doc.setLineWidth(0.2);
  doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
  
  doc.setFontSize(styles.fontSize.tiny);
  doc.setTextColor(styles.colors.secondary);
  
  doc.text("Pour vérifier l'authenticité: verify.wanzo.cd", margin, footerY);
  
  const copyright = "© Produit par Wanzo, développé par i-kiotahub";
  const copyrightWidth = doc.getTextWidth(copyright);
  doc.text(copyright, pageWidth - margin - copyrightWidth, footerY);

  doc.save(`${options.title.toLowerCase().replace(/\s+/g, '-')}.pdf`);
}

// Fonction utilitaire pour formater les nombres sans symbole de devise - Format bancaire
function formatNumber(value: any): string {
  if (value === undefined || value === null || value === '') return '-';
  // Gérer les séparateurs fr-CD/fr-FR: virgule décimale, point/espace milliers
  const cleaned = typeof value === 'string'
    ? value.replace(/\s/g, '').replace(/\.(?=\d{3})/g, '').replace(',', '.')
    : null;
  const num = cleaned !== null ? parseFloat(cleaned.replace(/[^\d.-]/g, '')) : value;
  if (isNaN(num)) return '-';
  
  // Format bancaire avec séparateur de milliers
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true
  }).format(num);
}

// Fonction pour extraire le nombre d'une chaîne formatée
function extractNumber(value: any): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^\d.-]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  }
  return 0;
}

// Fonction utilitaire professionnelle pour dessiner des tables avec autoTable
function drawTable(
  doc: jsPDF,
  headers: string[],
  rows: any[][],
  startY: number,
  options: {
    lastRowBold?: boolean;
    sectionRows?: number[]; // Indices des lignes de section (en gras, sans fond)
    columnStyles?: Record<number, any>;
    theme?: 'striped' | 'grid' | 'plain';
  } = {}
): number {
  const {
    lastRowBold = false,
    sectionRows = [],
    columnStyles = {},
    theme = 'striped'
  } = options;

  // Préparer les données formatées
  const formattedRows = rows.map((row, rowIndex) => {
    return row.map((cell, colIndex) => {
      // Pour les colonnes numériques (index >= 2), formater les nombres
      if (colIndex >= 2 && cell != null && cell !== '') {
        const num = extractNumber(cell);
        return num === 0 && cell === '' ? '' : formatNumber(num);
      }
      return cell || '';
    });
  });

  // Configuration des styles par défaut pour format bancaire
  const defaultColumnStyles: Record<number, any> = {};
  headers.forEach((_, index) => {
    if (index < 2) {
      defaultColumnStyles[index] = { 
        halign: 'left',
        cellPadding: { left: 2, right: 2 }
      };
    } else {
      defaultColumnStyles[index] = { 
        halign: 'right',
        cellPadding: { left: 2, right: 2 }
      };
    }
  });

  // Fusionner avec les styles personnalisés
  const finalColumnStyles = { ...defaultColumnStyles, ...columnStyles };

  autoTable(doc, {
    head: [headers],
    body: formattedRows,
    startY: startY,
    theme: theme,
    styles: {
      font: 'helvetica',
      fontSize: 8,
      cellPadding: 2,
      lineColor: [229, 231, 235],
      lineWidth: 0.1,
      textColor: [17, 24, 39],
      overflow: 'linebreak'
    },
    headStyles: {
      fillColor: [243, 244, 246],
      textColor: [17, 24, 39],
      fontStyle: 'bold',
      fontSize: 8,
      halign: 'center'
    },
    alternateRowStyles: {
      fillColor: [249, 250, 251]
    },
    columnStyles: finalColumnStyles,
    didParseCell: (data) => {
      // Ligne de section (sous-titre)
      if (data.section === 'body' && sectionRows.includes(data.row.index)) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [243, 244, 246];
        data.cell.styles.textColor = [55, 65, 81];
      }
      
      // Dernière ligne en gras
      if (data.section === 'body' && lastRowBold && data.row.index === formattedRows.length - 1) {
        data.cell.styles.fontStyle = 'bold';
        data.cell.styles.fillColor = [55, 65, 81];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontSize = 9;
      }
    },
    didDrawPage: (data) => {
      // Ajouter un numéro de page si nécessaire
      const pageCount = (doc as any).internal.getNumberOfPages();
      if (pageCount > 1) {
        doc.setFontSize(7);
        doc.setTextColor(107, 114, 128);
        doc.text(
          `Page ${(doc as any).internal.getCurrentPageInfo().pageNumber}/${pageCount}`,
          doc.internal.pageSize.width / 2,
          doc.internal.pageSize.height - 10,
          { align: 'center' }
        );
      }
    },
    margin: { top: 15, right: 15, bottom: 20, left: 15 },
    tableWidth: 'auto'
  });

  // Retourner la position Y finale
  return (doc as any).lastAutoTable.finalY + 5;
}

// Fonctions spécifiques pour chaque type d'état financier - Version bancaire professionnelle
function addBalanceSheetContent(
  doc: jsPDF,
  data: any,
  startY: number,
  styles: any,
  formatAmount: (amount: number | undefined) => string
): number {
  let y = startY;
  
  // Ajouter le titre ACTIF
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('ACTIF', 15, y);
  y += 8;
  
  // En-têtes du tableau d'actif
  const actifHeaders = ['Code', 'Libellé', 'Brut', 'Amort/Prov', 'Net', 'Net N-1'];
  
  // Données du tableau pour l'actif
  const actifRows: any[][] = [];
  const sectionRows: number[] = [];
  
  // Actif immobilisé
  actifRows.push(['', 'ACTIF IMMOBILISÉ', '', '', '', '']);
  sectionRows.push(actifRows.length - 1);
  
  if (data.fixedAssets?.intangibleAssets) {
    data.fixedAssets.intangibleAssets.forEach((item: any) => {
      actifRows.push([
        item.code || '',
        item.label || '',
        item.brut,
        item.amort,
        item.net,
        item.netN1
      ]);
    });
  }
  
  if (data.fixedAssets?.tangibleAssets) {
    data.fixedAssets.tangibleAssets.forEach((item: any) => {
      actifRows.push([
        item.code || '',
        item.label || '',
        item.brut,
        item.amort,
        item.net,
        item.netN1
      ]);
    });
  }
  
  if (data.fixedAssets?.total) {
    actifRows.push([
      '',
      'Total Actif immobilisé',
      data.fixedAssets.total.brut,
      data.fixedAssets.total.amort,
      data.fixedAssets.total.net,
      data.fixedAssets.total.netN1
    ]);
    sectionRows.push(actifRows.length - 1);
  }
  
  // Actif circulant
  actifRows.push(['', 'ACTIF CIRCULANT', '', '', '', '']);
  sectionRows.push(actifRows.length - 1);
  
  if (data.currentAssets?.inventory) {
    data.currentAssets.inventory.forEach((item: any) => {
      actifRows.push([
        item.code || '',
        item.label || '',
        item.brut,
        item.amort,
        item.net,
        item.netN1
      ]);
    });
  }
  
  if (data.currentAssets?.total) {
    actifRows.push([
      '',
      'Total Actif circulant',
      data.currentAssets.total.brut,
      data.currentAssets.total.amort,
      data.currentAssets.total.net,
      data.currentAssets.total.netN1
    ]);
    sectionRows.push(actifRows.length - 1);
  }
  
  // Total général
  if (data.totalAssets) {
    actifRows.push([
      '',
      'TOTAL ACTIF',
      data.totalAssets.brut,
      data.totalAssets.amort,
      data.totalAssets.net,
      data.totalAssets.netN1
    ]);
  }
  
  // Dessiner le tableau de l'actif avec autoTable
  y = drawTable(doc, actifHeaders, actifRows, y, {
    lastRowBold: true,
    sectionRows: sectionRows,
    theme: 'grid'
  });
  
  y += 10;
  
  // Ajouter le titre PASSIF
  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.text('PASSIF', 15, y);
  y += 8;
  
  // En-têtes du tableau de passif
  const passifHeaders = ['Code', 'Libellé', 'Net', 'Net N-1'];
  
  // Données du tableau pour le passif
  const passifRows: any[][] = [];
  const passifSectionRows: number[] = [];
  
  // Capitaux propres
  passifRows.push(['', 'CAPITAUX PROPRES', '', '']);
  passifSectionRows.push(passifRows.length - 1);
  
  if (data.equity?.capital) {
    data.equity.capital.forEach((item: any) => {
      passifRows.push([
        item.code || '',
        item.label || '',
        item.net,
        item.netN1
      ]);
    });
  }
  
  if (data.equity?.total) {
    passifRows.push([
      '',
      'Total Capitaux propres',
      data.equity.total.net,
      data.equity.total.netN1
    ]);
    passifSectionRows.push(passifRows.length - 1);
  }
  
  // Passif circulant
  if (data.liabilities) {
    passifRows.push(['', 'PASSIF CIRCULANT', '', '']);
    passifSectionRows.push(passifRows.length - 1);
    
    if (data.liabilities.debts) {
      data.liabilities.debts.forEach((item: any) => {
        passifRows.push([
          item.code || '',
          item.label || '',
          item.net,
          item.netN1
        ]);
      });
    }
    
    if (data.liabilities.total) {
      passifRows.push([
        '',
        'Total Passif circulant',
        data.liabilities.total.net,
        data.liabilities.total.netN1
      ]);
      passifSectionRows.push(passifRows.length - 1);
    }
  }
  
  // Total général
  if (data.totalLiabilities) {
    passifRows.push([
      '',
      'TOTAL PASSIF',
      data.totalLiabilities.net,
      data.totalLiabilities.netN1
    ]);
  }
  
  // Dessiner le tableau du passif avec autoTable
  y = drawTable(doc, passifHeaders, passifRows, y, {
    lastRowBold: true,
    sectionRows: passifSectionRows,
    theme: 'grid'
  });
  
  return y;
}

function addIncomeStatementContent(
  doc: jsPDF,
  data: any,
  startY: number,
  styles: any,
  formatAmount: (amount: number | undefined) => string
): number {
  let y = startY;
  
  // En-têtes du tableau
  const headers = ['Code', 'Libellé', 'Exercice N', 'Exercice N-1', 'Variation %'];
  
  // Données du tableau
  const rows: any[][] = [];
  const sectionRows: number[] = [];
  
  // Produits d'exploitation
  rows.push(['', "PRODUITS D'EXPLOITATION", '', '', '']);
  sectionRows.push(rows.length - 1);
  
  if (data.operatingIncome?.sales) {
    data.operatingIncome.sales.forEach((item: any) => {
      rows.push([
        item.code || '',
        item.label || '',
        item.current,
        item.previous,
        item.variation ? `${item.variation}` : ''
      ]);
    });
  }
  
  if (data.operatingIncome?.total) {
    rows.push([
      '',
      "Total Produits d'exploitation",
      data.operatingIncome.total.current,
      data.operatingIncome.total.previous,
      data.operatingIncome.total.variation ? `${data.operatingIncome.total.variation}` : ''
    ]);
    sectionRows.push(rows.length - 1);
  }
  
  // Charges d'exploitation
  rows.push(['', "CHARGES D'EXPLOITATION", '', '', '']);
  sectionRows.push(rows.length - 1);
  
  if (data.operatingExpenses?.purchases) {
    data.operatingExpenses.purchases.forEach((item: any) => {
      rows.push([
        item.code || '',
        item.label || '',
        item.current,
        item.previous,
        item.variation ? `${item.variation}` : ''
      ]);
    });
  }
  
  if (data.operatingExpenses?.total) {
    rows.push([
      '',
      "Total Charges d'exploitation",
      data.operatingExpenses.total.current,
      data.operatingExpenses.total.previous,
      data.operatingExpenses.total.variation ? `${data.operatingExpenses.total.variation}` : ''
    ]);
    sectionRows.push(rows.length - 1);
  }
  
  // Résultat net
  if (data.netResult) {
    rows.push([
      '',
      'RÉSULTAT NET',
      data.netResult.current,
      data.netResult.previous,
      data.netResult.variation ? `${data.netResult.variation}` : ''
    ]);
  }
  
  // Dessiner le tableau avec autoTable
  y = drawTable(doc, headers, rows, y, {
    lastRowBold: true,
    sectionRows: sectionRows,
    theme: 'grid',
    columnStyles: {
      4: { halign: 'center' } // Centrer la colonne variation
    }
  });
  
  return y;
}

function addCashFlowContent(
  doc: jsPDF,
  data: any,
  startY: number,
  styles: any,
  formatAmount: (amount: number | undefined) => string
): number {
  let y = startY;
  
  // En-têtes du tableau
  const headers = ['Libellé', 'Exercice N', 'Exercice N-1'];
  
  // Données du tableau
  const rows: any[][] = [];
  const sectionRows: number[] = [];
  
  // Flux de trésorerie liés à l'exploitation
  rows.push(["FLUX DE TRÉSORERIE LIÉS À L'EXPLOITATION", '', '']);
  sectionRows.push(rows.length - 1);
  
  if (data.operatingActivities?.items) {
    data.operatingActivities.items.forEach((item: any) => {
      rows.push([
        item.label || '',
        item.amount,
        item.amountN1
      ]);
    });
  }
  
  if (data.operatingActivities?.total) {
    rows.push([
      "Total des flux de trésorerie liés à l'exploitation",
      data.operatingActivities.total,
      data.operatingActivities.totalN1
    ]);
    sectionRows.push(rows.length - 1);
  }
  
  // Flux de trésorerie liés aux investissements
  if (data.investingActivities) {
    rows.push(["FLUX DE TRÉSORERIE LIÉS AUX INVESTISSEMENTS", '', '']);
    sectionRows.push(rows.length - 1);
    
    if (data.investingActivities.items) {
      data.investingActivities.items.forEach((item: any) => {
        rows.push([
          item.label || '',
          item.amount,
          item.amountN1
        ]);
      });
    }
    
    if (data.investingActivities.total) {
      rows.push([
        "Total des flux de trésorerie liés aux investissements",
        data.investingActivities.total,
        data.investingActivities.totalN1
      ]);
      sectionRows.push(rows.length - 1);
    }
  }
  
  // Flux de trésorerie liés au financement
  if (data.financingActivities) {
    rows.push(["FLUX DE TRÉSORERIE LIÉS AU FINANCEMENT", '', '']);
    sectionRows.push(rows.length - 1);
    
    if (data.financingActivities.items) {
      data.financingActivities.items.forEach((item: any) => {
        rows.push([
          item.label || '',
          item.amount,
          item.amountN1
        ]);
      });
    }
    
    if (data.financingActivities.total) {
      rows.push([
        "Total des flux de trésorerie liés au financement",
        data.financingActivities.total,
        data.financingActivities.totalN1
      ]);
      sectionRows.push(rows.length - 1);
    }
  }
  
  // Variation nette de trésorerie
  if (data.netCashChange !== undefined) {
    rows.push([
      'VARIATION NETTE DE TRÉSORERIE',
      data.netCashChange,
      data.netCashChangeN1
    ]);
  }
  
  // Dessiner le tableau avec autoTable
  y = drawTable(doc, headers, rows, y, {
    lastRowBold: true,
    sectionRows: sectionRows,
    theme: 'grid',
    columnStyles: {
      0: { cellWidth: 110 }
    }
  });
  
  return y;
}

/** Rendu PDF de la balance générale SYSCOHADA */
function addTrialBalanceContent(
  doc: jsPDF,
  data: any,
  startY: number,
  styles: any,
  formatAmount: (n: number | undefined) => string
): number {
  const has6Cols = data?.format === '6-columns-syscohada';
  const items: any[] = data?.items ?? Object.values(data?.byClass ?? {}).flat();
  const totals = data?.totals ?? {};

  const head = has6Cols
    ? [['Code', 'Intitulé', 'Ouv. Débit', 'Ouv. Crédit', 'Mvt Débit', 'Mvt Crédit', 'S. Débiteur', 'S. Créditeur']]
    : [['Code', 'Intitulé', 'Mvt Débit', 'Mvt Crédit', 'S. Débiteur', 'S. Créditeur']];

  const body = [
    ...items.map((item) =>
      has6Cols
        ? [item.account.code, item.account.name, formatAmount(item.opening?.debit), formatAmount(item.opening?.credit), formatAmount(item.movements?.debit), formatAmount(item.movements?.credit), formatAmount(item.balance?.debitBalance), formatAmount(item.balance?.creditBalance)]
        : [item.account.code, item.account.name, formatAmount(item.movements?.debit), formatAmount(item.movements?.credit), formatAmount(item.balance?.debitBalance), formatAmount(item.balance?.creditBalance)]
    ),
    has6Cols
      ? ['', 'TOTAUX', formatAmount(totals.totalOpeningDebit), formatAmount(totals.totalOpeningCredit), formatAmount(totals.totalDebit), formatAmount(totals.totalCredit), formatAmount(totals.totalDebitBalance), formatAmount(totals.totalCreditBalance)]
      : ['', 'TOTAUX', formatAmount(totals.totalDebit), formatAmount(totals.totalCredit), formatAmount(totals.totalDebitBalance), formatAmount(totals.totalCreditBalance)],
  ];

  autoTable(doc, {
    head,
    body,
    startY,
    styles: { fontSize: 7, cellPadding: 2 },
    headStyles: { fillColor: [55, 65, 81], textColor: 255, fontStyle: 'bold' },
    columnStyles: has6Cols
      ? { 0: { cellWidth: 18 }, 1: { cellWidth: 40 } }
      : { 0: { cellWidth: 18 }, 1: { cellWidth: 55 } },
    didDrawPage: (_d: any) => { startY = _d.cursor?.y ?? startY; },
  });

  return (doc as any).lastAutoTable?.finalY ?? startY;
}

async function exportToExcel(options: ExportOptions): Promise<void> {
  const { data, type, title, organization, currency } = options;

  const wb = XLSX.utils.book_new();
  
  const formattedData = formatDataForExcel(data, type, currency, title, organization);

  const ws = XLSX.utils.aoa_to_sheet(formattedData);

  // Set column widths
  if (formattedData.length > 0) {
    const colWidths = formattedData[0].map((_, i) => ({ wch: i === 1 ? 50 : 18 }));
    ws['!cols'] = colWidths;
  }

  XLSX.utils.book_append_sheet(wb, ws, 'Rapport');

  // Add metadata
  wb.Props = {
    Title: title,
    Subject: `Rapport Financier - ${type}`,
    Author: organization.name,
    CreatedDate: new Date()
  };

  XLSX.writeFile(wb, `${title.toLowerCase().replace(/\s+/g, '-')}.xlsx`);
}

// Helper for balance sheet
function getBalanceSheetData(data: any, formatValue: (v: any) => any): any[][] {
    if (!data) return [];
    return [
        ['ACTIF'],
        ['Code', 'Libellé', 'Brut', 'Amort/Prov', 'Net', 'Net N-1'],
        ['', 'Actif immobilisé'],
        ...(data.fixedAssets?.intangibleAssets?.map((item:any) => [item.code, item.label, formatValue(item.brut), formatValue(item.amort), formatValue(item.net), formatValue(item.netN1)]) || []),
        ...(data.fixedAssets?.tangibleAssets?.map((item:any) => [item.code, item.label, formatValue(item.brut), formatValue(item.amort), formatValue(item.net), formatValue(item.netN1)]) || []),
        ['', 'Total Actif immobilisé', formatValue(data.fixedAssets?.total?.brut), formatValue(data.fixedAssets?.total?.amort), formatValue(data.fixedAssets?.total?.net), formatValue(data.fixedAssets?.total?.netN1)],
        ['', 'Actif circulant'],
        ...(data.currentAssets?.inventory?.map((item:any) => [item.code, item.label, formatValue(item.brut), formatValue(item.amort), formatValue(item.net), formatValue(item.netN1)]) || []),
        ['', 'Total Actif circulant', formatValue(data.currentAssets?.total?.brut), formatValue(data.currentAssets?.total?.amort), formatValue(data.currentAssets?.total?.net), formatValue(data.currentAssets?.total?.netN1)],
        ['', 'TOTAL ACTIF', formatValue(data.totalAssets?.brut), formatValue(data.totalAssets?.amort), formatValue(data.totalAssets?.net), formatValue(data.totalAssets?.netN1)],
        [],
        ['PASSIF'],
        ['Code', 'Libellé', 'Net', 'Net N-1'],
        ['', 'Capitaux propres'],
        ...(data.equity?.capital?.map((item:any) => [item.code, item.label, formatValue(item.net), formatValue(item.netN1)]) || []),
        ['', 'Total Capitaux propres', formatValue(data.equity?.total?.net), formatValue(data.equity?.total?.netN1)],
        ['', 'Passif circulant'],
        ...(data.liabilities?.debts?.map((item:any) => [item.code, item.label, formatValue(item.net), formatValue(item.netN1)]) || []),
        ['', 'Total Passif circulant', formatValue(data.liabilities?.total?.net), formatValue(data.liabilities?.total?.netN1)],
        ['', 'TOTAL PASSIF', formatValue(data.totalLiabilities?.net), formatValue(data.totalLiabilities?.netN1)],
    ];
}

// Helper for income statement
function getIncomeStatementData(data: any, formatValue: (v: any) => any): any[][] {
    if (!data) return [];
    return [
        ['Code', 'Libellé', 'Montant', 'Montant N-1'],
        ['', "Produits d'exploitation"],
        ...(data.operatingIncome?.items?.map((item:any) => [item.code, item.label, formatValue(item.amount), formatValue(item.amountN1)]) || []),
        ['', "Total Produits d'exploitation", formatValue(data.operatingIncome?.total), formatValue(data.operatingIncome?.totalN1)],
        ['', "Charges d'exploitation"],
        ...(data.operatingExpenses?.items?.map((item:any) => [item.code, item.label, formatValue(item.amount), formatValue(item.amountN1)]) || []),
        ['', "Total Charges d'exploitation", formatValue(data.operatingExpenses?.total), formatValue(data.operatingExpenses?.totalN1)],
        ['', "Résultat d'exploitation", formatValue(data.operatingResult), formatValue(data.operatingResultN1)],
        ['', 'Résultat Net', formatValue(data.netResult), formatValue(data.netResultN1)],
    ];
}

// Helper for cash flow statement
function getCashFlowStatementData(data: any, formatValue: (v: any) => any): any[][] {
    if (!data) return [];
    return [
        ['Libellé', 'Montant'],
        ["Flux de trésorerie liés à l'exploitation"],
        ...(data.operatingActivities?.items?.map((item:any) => [item.label, formatValue(item.amount)]) || []),
        ["Total des flux de trésorerie liés à l'exploitation", formatValue(data.operatingActivities?.total)],
        ["Flux de trésorerie liés aux investissements"],
        ...(data.investingActivities?.items?.map((item:any) => [item.label, formatValue(item.amount)]) || []),
        ["Total des flux de trésorerie liés aux investissements", formatValue(data.investingActivities?.total)],
        ["Flux de trésorerie liés au financement"],
        ...(data.financingActivities?.items?.map((item:any) => [item.label, formatValue(item.amount)]) || []),
        ["Total des flux de trésorerie liés au financement", formatValue(data.financingActivities?.total)],
        [],
        ['Variation nette de trésorerie', formatValue(data.netCashChange)],
        ["Trésorerie à l'ouverture", formatValue(data.openingCash)],
        ["Trésorerie à la clôture", formatValue(data.closingCash)],
    ];
}

/** Données Excel de la balance générale */
function getTrialBalanceExcelData(data: any, formatValue: (v: any) => string): any[][] {
    const has6Cols = data?.format === '6-columns-syscohada';
    const items: any[] = data?.items ?? Object.values(data?.byClass ?? {}).flat();
    const totals = data?.totals ?? {};

    const headerRow = has6Cols
        ? ['Code', 'Intitulé', 'Ouv. Débit', 'Ouv. Crédit', 'Mvt Débit', 'Mvt Crédit', 'S. Débiteur', 'S. Créditeur']
        : ['Code', 'Intitulé', 'Mvt Débit', 'Mvt Crédit', 'S. Débiteur', 'S. Créditeur'];

    const rows = items.map((item) =>
        has6Cols
            ? [item.account.code, item.account.name, formatValue(item.opening?.debit ?? 0), formatValue(item.opening?.credit ?? 0), formatValue(item.movements?.debit ?? 0), formatValue(item.movements?.credit ?? 0), formatValue(item.balance?.debitBalance ?? 0), formatValue(item.balance?.creditBalance ?? 0)]
            : [item.account.code, item.account.name, formatValue(item.movements?.debit ?? 0), formatValue(item.movements?.credit ?? 0), formatValue(item.balance?.debitBalance ?? 0), formatValue(item.balance?.creditBalance ?? 0)]
    );

    const totalsRow = has6Cols
        ? ['', 'TOTAUX', formatValue(totals.totalOpeningDebit), formatValue(totals.totalOpeningCredit), formatValue(totals.totalDebit), formatValue(totals.totalCredit), formatValue(totals.totalDebitBalance), formatValue(totals.totalCreditBalance)]
        : ['', 'TOTAUX', formatValue(totals.totalDebit), formatValue(totals.totalCredit), formatValue(totals.totalDebitBalance), formatValue(totals.totalCreditBalance)];

    return [headerRow, ...rows, totalsRow];
}

function formatDataForExcel(
    data: any, 
    type: FinancialStatementType, 
    currency: CurrencyCode,
    title: string,
    organization: any
): any[][] {
    const header = [
        [organization.name],
        [`RCCM: ${organization.registrationNumber || 'N/A'} | ID. NAT: ${organization.taxId || 'N/A'}`],
        [title],
        [`Exercice clos le 31 décembre ${data?.exercice || new Date().getFullYear()}`],
        [`Devise: ${currency}`],
        [] // Empty row for spacing
    ];

    const footer = [
        [], // Empty row
        ["© Produit par Wanzo, développé par i-kiotahub"]
    ];
    
    const formatValue = (value: any) => {
        if (typeof value === 'number') {
            return value;
        }
        if (value === undefined || value === null) {
            return ''; // Return empty string for undefined/null values
        }
        return value;
    };

    let tableData: any[][] = [];

    switch (type) {
        case 'balance':
            tableData = getBalanceSheetData(data, formatValue);
            break;
        case 'income':
            tableData = getIncomeStatementData(data, formatValue);
            break;
        case 'cashflow':
            tableData = getCashFlowStatementData(data, formatValue);
            break;
        case 'trial-balance':
            tableData = getTrialBalanceExcelData(data, formatValue);
            break;
    }

    return [...header, ...tableData, ...footer];
}